import apiClient from "@/lib/api";
import type { LatestDocumentReview } from "@/lib/document-review/types";

export type JobHuntingFileType = "resume" | "career_sheet" | "entry_sheet";

export type JobHuntingFileExtractionStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed";

export const JOB_HUNTING_FILE_TYPE_LABELS: Record<JobHuntingFileType, string> = {
  resume: "履歴書",
  career_sheet: "職務経歴書",
  entry_sheet: "エントリーシート",
};

export interface JobHuntingFile {
  id: string;
  title: string;
  file_type: JobHuntingFileType;
  industry_id: string | null;
  memo: string | null;
  byte_size: number;
  extracted_text_ready: boolean;
  extraction_status: JobHuntingFileExtractionStatus;
  extraction_failure_reason: string | null;
  /**
   * 最新の添削（#1866）。一覧の応答にのみ含まれ、添削が一度も無い書類では null になる。
   * 単体取得の応答には鍵ごと現れない。
   */
  latest_review?: LatestDocumentReview | null;
  created_at: string;
  updated_at: string;
}

export interface PaginationMeta {
  current_page: number;
  per_page: number;
  total_count: number;
  total_pages: number;
}

export interface JobHuntingFilesListResponse {
  items: JobHuntingFile[];
  meta: PaginationMeta;
}

export interface ListParams {
  page?: number;
  per_page?: number;
  file_type?: JobHuntingFileType;
}

export interface UpdatePayload {
  title?: string;
  file_type?: JobHuntingFileType;
  industry_id?: string | null;
  memo?: string | null;
}

export interface CreatePayload {
  file: File;
  title: string;
  file_type: JobHuntingFileType;
  industry_id?: string | null;
  memo?: string | null;
}

export async function listJobHuntingFiles(
  params: ListParams = {}
): Promise<JobHuntingFilesListResponse> {
  const res = await apiClient.get<JobHuntingFilesListResponse>(
    "/job_hunting_files",
    { params }
  );
  return res.data;
}

export async function createJobHuntingFile(
  payload: CreatePayload
): Promise<JobHuntingFile> {
  const formData = new FormData();
  formData.append("file", payload.file);
  formData.append("title", payload.title);
  formData.append("file_type", payload.file_type);
  if (payload.industry_id) {
    formData.append("industry_id", payload.industry_id);
  }
  if (payload.memo) {
    formData.append("memo", payload.memo);
  }

  const res = await apiClient.post<JobHuntingFile>(
    "/job_hunting_files",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res.data;
}

export async function updateJobHuntingFile(
  id: string,
  payload: UpdatePayload
): Promise<JobHuntingFile> {
  const res = await apiClient.patch<JobHuntingFile>(
    `/job_hunting_files/${id}`,
    payload
  );
  return res.data;
}

export async function deleteJobHuntingFile(id: string): Promise<void> {
  await apiClient.delete(`/job_hunting_files/${id}`);
}

export async function fetchJobHuntingFile(id: string): Promise<JobHuntingFile> {
  const res = await apiClient.get<JobHuntingFile>(`/job_hunting_files/${id}`);
  return res.data;
}

export async function checkDuplicateTitle(
  title: string
): Promise<{ duplicate: boolean }> {
  const res = await apiClient.get<{ duplicate: boolean }>(
    "/job_hunting_files/check_duplicate",
    { params: { title } }
  );
  return res.data;
}

const BLOB_URL_LIFETIME_MS = 60_000;

/**
 * ファイル本体をそのまま取得する。
 *
 * 画面内へ埋め込むプレビューで使う。表示している間ずっと URL を生きたままにする必要があり、
 * 一定時間で解放する fetchJobHuntingFileContentBlobUrl では寿命が合わないため、
 * オブジェクト URL の生成と解放は呼び出し側に委ねる。
 */
export async function fetchJobHuntingFileContentBlob(id: string): Promise<Blob> {
  const res = await apiClient.get<Blob>(`/job_hunting_files/${id}/content`, {
    responseType: "blob",
  });
  return res.data;
}

export async function fetchJobHuntingFileContentBlobUrl(
  id: string
): Promise<string> {
  const blob = await fetchJobHuntingFileContentBlob(id);
  const url = URL.createObjectURL(blob);
  // 新規タブが PDF を読み込み終わるまでの猶予を持たせて解放（メモリリーク防止）。
  // 解放後も既に読み込まれた PDF タブの表示は維持される。
  setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_LIFETIME_MS);
  return url;
}
